import type { SensitivityLevel } from '@dawn/feature-config';

import { cn } from '../../lib/utils';

export type { SensitivityLevel }; // re-export for backward compat

export type SensitivityChipSize = 'sm' | 'lg';

/**
 * Orange-ramp class map for the five canonical sensitivity levels per Figma
 * `289:50781`. All five tints are points on the same orange scale (200..600);
 * lighter levels keep dark text, heavier levels flip to white. The unclassified
 * remainder sits off that ramp on purpose.
 */
const LEVEL_STYLES = {
  'highly-confidential': 'bg-[#F47600] text-dawn-white',
  restricted: 'bg-[#FF9700] text-dawn-white',
  confidential: 'bg-[#FFBB60] text-dawn-black',
  internal: 'bg-[#FFDBA9] text-dawn-black',
  public: 'bg-[#FFEACE] text-dawn-black',
  // Not a tier. Grey-400, the same value the donut and pipe popover draw the
  // unclassified remainder with, so the swatch identifies its own slice.
  unclassified: 'bg-[#8D8D8D] text-dawn-white',
} as const satisfies Readonly<Record<string, string>>;

const FALLBACK_STYLE = 'bg-grey-100 text-grey-500';

/**
 * Size variants. `lg` (default) is 13px regular per Figma `289:50781`; `sm` is
 * 10px capitalized per Figma `289:50792` (typography flip; same orange ramp).
 *
 * Note: typography uses bracketed font-size literals (`text-[13px]`, `text-[10px]`)
 * rather than the `text-p1` / `text-p4` named tokens, because `tailwind-merge`'s
 * default groupings cannot disambiguate custom token names from `text-*` color
 * utilities — the named font-size token would silently strip out the
 * `text-dawn-{white|black}` color on the same element.
 */
const SIZE_STYLES = {
  lg: 'text-[13px] font-normal leading-[1.3] px-1 inline-flex h-[18px] items-center justify-center whitespace-nowrap',
  sm: 'text-[10px] font-normal leading-[1.4] capitalize px-1.5 py-0.5 whitespace-nowrap',
} as const satisfies Readonly<Record<SensitivityChipSize, string>>;

export interface SensitivityChipProps {
  /** Stable id from the API (lowercase-kebab). Drives tint. */
  level: string;
  /** Display label from the API ("Highly Confidential" etc.). */
  label: string;
  /** Typography variant: `lg` = 13px regular, `sm` = 10px capitalized. */
  size?: SensitivityChipSize;
  className?: string;
}

export function SensitivityChip({ level, label, size = 'lg', className }: SensitivityChipProps) {
  const tint = LEVEL_STYLES[level as keyof typeof LEVEL_STYLES] ?? FALLBACK_STYLE;

  return (
    <span
      data-testid="sensitivity-chip"
      data-level={level}
      className={cn(
        'inline-flex items-center rounded-[4px] leading-none',
        tint,
        SIZE_STYLES[size],
        className
      )}
    >
      {label}
    </span>
  );
}

SensitivityChip.displayName = 'SensitivityChip';
